/**
 * 轻量调试日志总线 — AI 科学圆桌专用
 *
 * 设计目标：
 *   - 把分散在 ExpertAvatar / ScienceExpoStage / ScienceExpoDigitalHuman 的关键状态
 *     汇集到一个时序面板，方便现场调试，不再被 console.log 海量输出淹没
 *   - 默认关闭，零运行时成本（emit 时检查 enabled，关闭直接 return）
 *   - 通过 localStorage.expoDebug = "1" 开启，或 Ctrl+Shift+D 快捷键切换
 *   - 任意组件可订阅，DebugConsole 是默认 UI
 */

const MAX_BUFFER = 200;

let enabled = false;
try {
  enabled = typeof window !== "undefined" && window.localStorage?.getItem("expoDebug") === "1";
} catch (_) { /* SSR/隐私模式 */ }

const buffer = [];
const subscribers = new Set();

const dispatch = (entry) => {
  buffer.push(entry);
  if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
  subscribers.forEach((fn) => {
    try { fn(entry, buffer); } catch (_) {}
  });
};

const fmtTs = (date) => {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  const s = String(date.getSeconds()).padStart(2, "0");
  const ms = String(date.getMilliseconds()).padStart(3, "0");
  return `${h}:${m}:${s}.${ms}`;
};

export const debugLog = {
  isEnabled: () => enabled,

  setEnabled(v) {
    enabled = !!v;
    try { window.localStorage?.setItem("expoDebug", enabled ? "1" : "0"); } catch (_) {}
    subscribers.forEach((fn) => {
      try { fn(null, buffer); } catch (_) {}
    });
  },

  toggle() { this.setEnabled(!enabled); },

  /**
   * @param {"PRELOAD"|"SESSION"|"WS"|"SPEAKER"|"TTS"|"ASR"|"RENDER"|"STATE"|string} category
   * @param {string} msg
   * @param {"info"|"warn"|"error"} level
   */
  emit(category, msg, level = "info") {
    if (!enabled) return;
    const ts = new Date();
    dispatch({
      ts: ts.getTime(),
      tsLabel: fmtTs(ts),
      category: String(category || "MISC").toUpperCase(),
      level,
      msg: String(msg ?? ""),
    });
  },

  /** 订阅日志变化，返回取消订阅函数 */
  subscribe(fn) {
    subscribers.add(fn);
    fn(null, buffer);
    return () => subscribers.delete(fn);
  },

  snapshot: () => buffer.slice(),

  clear() {
    buffer.length = 0;
    subscribers.forEach((fn) => {
      try { fn(null, buffer); } catch (_) {}
    });
  },
};

// 开发期全局快捷键 Ctrl+Shift+D 开关调试
if (typeof window !== "undefined" && !window.__expoDebugBound) {
  window.__expoDebugBound = true;
  window.addEventListener("keydown", (e) => {
    if (e.ctrlKey && e.shiftKey && (e.key === "D" || e.key === "d")) {
      debugLog.toggle();
      console.log(`[debugLog] ${debugLog.isEnabled() ? "已开启" : "已关闭"}调试面板`);
      e.preventDefault();
    }
  });
}

export default debugLog;
